import {ABILITY_CAST_COLORS as COLORS, ABILITY_CAST_TEXTURE_KEYS} from '../../configs/abilityCastAnimation';
import {MELEE_TEXTURE_KEYS} from '../../configs/meleeAnimation';
import {CELL_OUTLINE_TEXTURE_KEYS} from '../../configs/fieldCellOutlines';
import {HEX_WAVE_TEXTURE_KEYS} from '../../configs/unitRecruitmentAnimation';
import {WINGED_FLIGHT, WINGED_FLIGHT_TEXTURE_KEYS} from '../../configs/wingedFlightAnimation';
import {
  clamp01,
  createElement,
  easeInOutSine,
  easeOutBack,
  easeOutCubic,
  easeOutQuad,
  fadeInOut,
  lerp,
  mixColor,
} from './buildAbilityCast';
import {ABILITY_CAST_TEXTURE_SIZES} from './abilityCastTextures';
import {HEX_WAVE_TEXTURE_SIZE, resolveHexWaveScale} from './hexWaveTextures';

const TWO_PI = Math.PI * 2;
const {igniteMs, landMs, body: BODY, wing: WING} = WINGED_FLIGHT;

const STREAK_SIZE = ABILITY_CAST_TEXTURE_SIZES[ABILITY_CAST_TEXTURE_KEYS.STREAK];
const STREAK_ASPECT = STREAK_SIZE.height / STREAK_SIZE.width;
// Длина следа задается масштабом от этой ширины
const STREAK_BASE_PX = 100;

// Разгон и торможение полета
const easeInOutCubic = (share) => (share < 0.5 ? 4 * share ** 3 : 1 - ((2 - 2 * share) ** 3) / 2);

/** Длительность самого полета по расстоянию */
export const resolveWingedFlightMs = (distance, hexSize) => {
  const {baseMs, perHexSizeMs, minMs, maxMs} = WINGED_FLIGHT.flight;
  return Math.round(Math.min(maxMs, Math.max(minMs, baseMs + perHexSizeMs * (distance / hexSize))));
};

/**
 * Удар крыла в момент elapsedMs: 1 — верхняя точка, -1 — нижняя. Вниз крыло идет
 * быстрее, чем обратно
 */
export const resolveFlapWave = (elapsedMs) => {
  const phase = ((((elapsedMs - igniteMs) / WING.flapPeriodMs) % 1) + 1) % 1;
  const {flapDownShare} = WING;
  return phase < flapDownShare
    ? Math.cos(Math.PI * (phase / flapDownShare))
    : Math.cos(Math.PI * (1 + (phase - flapDownShare) / (1 - flapDownShare)));
};

/**
 * Поза одного крыла без учета стороны и наклона: поворот от образца, масштаб
 * размаха и видимость. Три участка: раскрытие из сложенного положения,
 * взмахи, складывание после посадки
 *
 * @param {number} elapsedMs
 * @param {number} impactMs — посадка
 * @returns {{angle: number, spanScale: number, chordScale: number, alpha: number, heat: number}}
 *   heat — насколько крыло раскалено добела: 1 при загорании, 0 в полете
 */
export const resolveWingPose = (elapsedMs, impactMs) => {
  const {foldedAngle, raisedAngle, loweredAngle, foldedScale, strokeShorten} = WING;
  const middleAngle = (raisedAngle + loweredAngle) / 2;
  const halfSwing = (loweredAngle - raisedAngle) / 2;

  if (elapsedMs < igniteMs) {
    const share = clamp01(elapsedMs / igniteMs);
    const open = easeOutBack(clamp01(share / 0.72));
    // К концу разгорания крыло уходит вверх: оттуда начинается первый удар
    return {
      angle: lerp(foldedAngle, raisedAngle, open),
      spanScale: lerp(foldedScale, 1, open),
      chordScale: lerp(foldedScale, 1, open),
      alpha: clamp01(share / 0.16),
      heat: 1 - easeInOutSine(clamp01((share - 0.25) / 0.75)),
    };
  }

  if (elapsedMs < impactMs) {
    const wave = resolveFlapWave(elapsedMs);
    return {
      angle: middleAngle - halfSwing * wave,
      spanScale: 1 - strokeShorten * clamp01((1 - wave) / 2),
      chordScale: 1,
      alpha: 1,
      heat: 0,
    };
  }

  // Посадка: последний подъем крыльев, затем они складываются и гаснут
  const share = clamp01((elapsedMs - impactMs) / WINGED_FLIGHT.land.wingFadeMs);
  const fold = easeInOutSine(clamp01((share - 0.2) / 0.8));
  const lastWave = resolveFlapWave(impactMs);
  const settleAngle = lerp(middleAngle - halfSwing * lastWave, raisedAngle, easeOutQuad(clamp01(share / 0.25)));
  return {
    angle: lerp(settleAngle, foldedAngle, fold),
    spanScale: lerp(1, foldedScale, fold),
    chordScale: lerp(1, foldedScale, fold),
    alpha: 1 - clamp01((share - 0.35) / 0.65),
    heat: clamp01(share * 2.2) * (1 - clamp01((share - 0.5) / 0.5)),
  };
};

/**
 * Где отряд в момент elapsedMs: точка на отрезке от центра до центра ячейки и
 * подъем над тенью. Этой же функцией идут и крылья, и сама иконка отряда
 *
 * @returns {{x: number, y: number, lift: number}}
 */
const createUnitPoseResolver = ({from, to, hexSize, impactMs, flightMs}) => (elapsedMs) => {
  const share = clamp01((elapsedMs - igniteMs) / flightMs);
  const moved = easeInOutCubic(share);
  const liftPx = hexSize * BODY.liftFactor;

  let lift;
  if (elapsedMs < igniteMs) {
    // Приседание перед взлетом, затем подъем
    const ignite = clamp01(elapsedMs / igniteMs);
    const crouch = -hexSize * BODY.crouchFactor * Math.sin(Math.PI * clamp01(ignite / 0.45));
    lift = crouch + liftPx * easeInOutSine(clamp01((ignite - 0.4) / 0.6));
  } else if (elapsedMs < impactMs) {
    const descend = clamp01((share - BODY.descendFromShare) / (1 - BODY.descendFromShare));
    // Качается в такт взмахам и успокаивается к посадке
    const bob = hexSize * BODY.bobFactor * resolveFlapWave(elapsedMs) * (1 - descend) * clamp01(share * 6);
    lift = liftPx * (1 - easeInOutSine(descend)) + bob;
  } else {
    lift = 0;
  }

  return {x: lerp(from.x, to.x, moved), y: lerp(from.y, to.y, moved), lift};
};

/**
 * ПОЛЕТ ОТРЯДА НА КРЫЛЬЯХ (см. WINGED_FLIGHT): план того же вида, что у
 * способностей (см. buildAbilityCast). impactMs — посадка: отряд на месте, отсюда
 * на поле начинается настоящий отряд на целевой ячейке, а крылья и волна еще
 * досветят поверх него.
 *
 * Иконку отряда план не рисует: ее ведет вызывающий по plan.resolveUnitPose.
 *
 * @param {Object} params
 * @param {{center: {x, y}, ground: {x, y}}} params.from — опорные точки исходной ячейки
 * @param {{center: {x, y}, ground: {x, y}}} params.to — опорные точки целевой ячейки
 * @param {number} params.hexSize
 * @param {number} [params.hexRotation] — поворот шестиугольных колец под вершину ячейки
 * @param {function(): number} [params.rng]
 */
export const buildWingedFlightCastPlan = ({from, to, hexSize, hexRotation = 0, rng = Math.random}) => {
  const size = hexSize;
  const start = from.center;
  const end = to.center;
  const distance = Math.hypot(end.x - start.x, end.y - start.y);
  const flightMs = resolveWingedFlightMs(distance, size);
  const impactMs = igniteMs + flightMs;
  const directionX = distance > 0 ? (end.x - start.x) / distance : 0;
  const directionY = distance > 0 ? (end.y - start.y) / distance : 0;
  const flightRotation = Math.atan2(directionY, directionX);
  const distanceInHexSizes = distance / size;
  const resolveUnitPose = createUnitPoseResolver({from: start, to: end, hexSize: size, impactMs, flightMs});
  const afterImpact = (elapsedMs, durationMs) => clamp01((elapsedMs - impactMs) / durationMs);
  const gold = COLORS.upgradeGold;
  const light = COLORS.upgradeLight;
  const pale = mixColor(light, COLORS.white, 0.6);
  const pickSparkTint = (index) => [gold, light, pale][index % 3];
  const hexScale = (radiusFactor) => resolveHexWaveScale(size * radiusFactor);
  const ringSizePx = HEX_WAVE_TEXTURE_SIZE;
  const elements = [];

  // Обе точки плеч: крылья растут из-за иконки и поднимаются вместе с ней
  const resolveShoulder = (side, elapsedMs) => {
    const pose = resolveUnitPose(elapsedMs);
    return {
      x: pose.x + side * size * WING.shoulderXFactor,
      y: pose.y - pose.lift + size * WING.shoulderYFactor,
    };
  };

  // В полете крылья чуть наклонены в сторону движения
  const resolveBank = (elapsedMs) => {
    const envelope = clamp01((elapsedMs - igniteMs) / 160) * (1 - clamp01((elapsedMs - impactMs + 160) / 160));
    return WING.bank * directionX * envelope;
  };

  // После посадки настоящий отряд уже на своем слое: то, что было за поднятой
  // иконкой, уходит ему за спину
  const resolveWingsLayer = (elapsedMs) => (elapsedMs < impactMs ? 'wings' : 'wingsLanded');

  // Спрайт крыла в момент poseMs: левое — то же правое с отрицательным масштабом.
  // Слой — по настоящему времени: у следа-повтора poseMs отстает
  const resolveWingSpritePose = (side, poseMs, elapsedMs = poseMs) => {
    const wing = resolveWingPose(poseMs, impactMs);
    return {
      ...resolveShoulder(side, poseMs),
      scaleX: side * wing.spanScale,
      scaleY: wing.chordScale,
      rotation: side * wing.angle + resolveBank(poseMs),
      layer: resolveWingsLayer(elapsedMs),
      wing,
    };
  };

  const wingEndMs = impactMs + WINGED_FLIGHT.land.wingFadeMs;
  const wingElement = (element) => createElement({
    layer: 'wings',
    origin: WING.origin,
    sizePx: size * WING.sizeFactor,
    startMs: 0,
    durationMs: wingEndMs,
    ...element,
  });

  // ЗЕМЛЯ ПОД ОТРЯДОМ: свет разгорается и уходит со взлетом
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: gold, sizePx: size * WINGED_FLIGHT.ignite.auraSizeFactor,
    startMs: 0, durationMs: igniteMs + WINGED_FLIGHT.launch.ringMs, layer: 'ground', blend: 'add',
    pose: (local, elapsedMs) => {
      const share = clamp01(elapsedMs / igniteMs);
      const leave = clamp01((elapsedMs - igniteMs) / WINGED_FLIGHT.launch.ringMs);
      return {
        ...from.ground,
        scale: lerp(0.5, 1, easeOutQuad(share)) * (1 + 0.25 * leave),
        alpha: WINGED_FLIGHT.ignite.auraAlpha * share * (1 - leave),
      };
    },
  }));

  // Контур ячейки и знак на земле
  [CELL_OUTLINE_TEXTURE_KEYS.GLOW, HEX_WAVE_TEXTURE_KEYS.CORE].forEach((textureKey, index) => {
    elements.push(createElement({
      textureKey, tint: index ? pale : gold, sizePx: ringSizePx * hexScale(1),
      startMs: 0, durationMs: igniteMs + WINGED_FLIGHT.launch.flashMs, layer: 'ground', order: 1 + index,
      pose: (local, elapsedMs) => ({
        ...start,
        rotation: hexRotation,
        alpha: elapsedMs < igniteMs
          ? (elapsedMs / igniteMs) ** 1.5
          : 1 - clamp01((elapsedMs - igniteMs) / WINGED_FLIGHT.launch.flashMs),
      }),
    }));
  });
  [1, -1].forEach((direction, index) => {
    const {runeRadiusFactor, runeTurn, runeAlpha} = WINGED_FLIGHT.ignite;
    elements.push(createElement({
      textureKey: CELL_OUTLINE_TEXTURE_KEYS.CORE_DASHED, tint: index ? light : gold,
      sizePx: ringSizePx * hexScale(runeRadiusFactor * (index ? 0.72 : 1)),
      startMs: 0, durationMs: igniteMs + WINGED_FLIGHT.launch.flashMs, layer: 'ground', order: 3 + index,
      pose: (local, elapsedMs) => {
        const share = clamp01(elapsedMs / igniteMs);
        const leave = clamp01((elapsedMs - igniteMs) / WINGED_FLIGHT.launch.flashMs);
        return {
          ...start,
          scale: lerp(1.35, 1, easeOutCubic(share)) * (1 + 0.5 * easeOutQuad(leave)),
          rotation: hexRotation + direction * runeTurn * share ** 2,
          alpha: runeAlpha * clamp01(share * 3) * (1 - leave),
        };
      },
    }));
  });

  // Кольца сходятся к отряду, последнее приходит к взлету
  {
    const {ringCount, ringFromFactor, ringLifeShare} = WINGED_FLIGHT.ignite;
    const ringLifeMs = igniteMs * ringLifeShare;
    for (let index = 0; index < ringCount; index += 1) {
      const ringStartMs = (igniteMs - ringLifeMs) * (ringCount > 1 ? index / (ringCount - 1) : 1);
      elements.push(createElement({
        textureKey: HEX_WAVE_TEXTURE_KEYS.CORE, tint: light, sizePx: ringSizePx * hexScale(1),
        startMs: ringStartMs, durationMs: ringLifeMs, order: 2,
        pose: (local) => ({
          ...start,
          scale: lerp(ringFromFactor, 0.55, local * local),
          rotation: hexRotation,
          alpha: clamp01(local / 0.25) * (local > 0.9 ? (1 - local) / 0.1 : 1) * 0.85,
        }),
      }));
    }
  }

  // Искры стягиваются к плечам: из них и загораются крылья
  {
    const {moteCount, moteFromFactor, moteSizeFactor, moteLifeShare, moteTurn} = WINGED_FLIGHT.ignite;
    const moteLifeMs = igniteMs * moteLifeShare;
    for (let index = 0; index < moteCount; index += 1) {
      const side = index % 2 ? 1 : -1;
      const angle = rng() * TWO_PI;
      const reach = size * moteFromFactor * (0.55 + 0.45 * rng());
      elements.push(createElement({
        textureKey: MELEE_TEXTURE_KEYS.BURST, tint: pickSparkTint(index), blend: 'add',
        sizePx: size * moteSizeFactor, startMs: (igniteMs - moteLifeMs) * (index / moteCount), durationMs: moteLifeMs, order: 6,
        pose: (local, elapsedMs) => {
          const shoulder = resolveShoulder(side, elapsedMs);
          const swirl = angle + moteTurn * local;
          const radius = reach * (1 - easeInOutSine(local));
          return {
            x: shoulder.x + Math.cos(swirl) * radius,
            y: shoulder.y + Math.sin(swirl) * radius * 0.8,
            scale: 0.55 + 0.65 * local,
            rotation: swirl,
            alpha: clamp01(local * 3) * (local > 0.88 ? (1 - local) / 0.12 : 1),
          };
        },
      }));
    }
  }

  // ОРЕОЛ И ЛУЧИ за отрядом: летят вместе с ним
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: gold, sizePx: size * WINGED_FLIGHT.halo.sizeFactor,
    startMs: 0, durationMs: wingEndMs, layer: 'wings', blend: 'add', order: -9,
    pose: (local, elapsedMs) => {
      const pose = resolveUnitPose(elapsedMs);
      const pulse = 1 + 0.12 * (elapsedMs < igniteMs ? 0 : resolveFlapWave(elapsedMs));
      return {
        x: pose.x,
        y: pose.y - pose.lift,
        scale: lerp(0.45, 1, easeOutQuad(clamp01(elapsedMs / igniteMs))) * pulse,
        layer: resolveWingsLayer(elapsedMs),
        alpha: WINGED_FLIGHT.halo.alpha * clamp01(elapsedMs / (igniteMs * 0.5))
          * (1 - afterImpact(elapsedMs, WINGED_FLIGHT.land.wingFadeMs)),
      };
    },
  }));
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.BURST, tint: light, sizePx: size * WINGED_FLIGHT.rays.sizeFactor,
    startMs: igniteMs * 0.45, durationMs: wingEndMs - igniteMs * 0.45, layer: 'wings', blend: 'add', order: -8,
    pose: (local, elapsedMs) => {
      const pose = resolveUnitPose(elapsedMs);
      return {
        x: pose.x,
        y: pose.y - pose.lift,
        rotation: WINGED_FLIGHT.rays.turn * local,
        layer: resolveWingsLayer(elapsedMs),
        alpha: WINGED_FLIGHT.rays.alpha * fadeInOut(local, 0.14, 0.3),
      };
    },
  }));

  // КРЫЛЬЯ: свечение, следы-повторы, само крыло и белый жар поверх него
  [1, -1].forEach((side) => {
    elements.push(wingElement({
      textureKey: WINGED_FLIGHT_TEXTURE_KEYS.WING_GLOW, tint: WING.underlayTint, order: -7.5,
      pose: (local, elapsedMs) => {
        const {wing, ...pose} = resolveWingSpritePose(side, elapsedMs);
        return {...pose, alpha: WING.underlayAlpha * wing.alpha};
      },
    }));
    elements.push(wingElement({
      textureKey: WINGED_FLIGHT_TEXTURE_KEYS.WING_GLOW, tint: gold, blend: 'add', order: -7,
      pose: (local, elapsedMs) => {
        const {wing, ...pose} = resolveWingSpritePose(side, elapsedMs);
        // Свечение сильнее при загорании и на ударе вниз
        const pulse = elapsedMs < igniteMs ? wing.heat : (1 - resolveFlapWave(elapsedMs)) / 2;
        return {...pose, alpha: WING.glowAlpha * wing.alpha * (1 - WING.glowPulse + WING.glowPulse * pulse)};
      },
    }));
    elements.push(wingElement({
      textureKey: WINGED_FLIGHT_TEXTURE_KEYS.WING_GLOW, tint: pale, blend: 'add', order: -6.5,
      pose: (local, elapsedMs) => {
        const {wing, ...pose} = resolveWingSpritePose(side, elapsedMs);
        return {...pose, alpha: 0.75 * wing.alpha * wing.heat};
      },
    }));
    WING.ghosts.forEach(({lagMs, alpha}, index) => {
      elements.push(wingElement({
        textureKey: WINGED_FLIGHT_TEXTURE_KEYS.WING, blend: 'add', order: -6 + index * 0.1,
        startMs: igniteMs, durationMs: wingEndMs - igniteMs,
        pose: (local, elapsedMs) => {
          const {wing, ...pose} = resolveWingSpritePose(side, elapsedMs - lagMs, elapsedMs);
          // Следа нет, пока отряд не взлетел, и на посадке он догоняет крыло
          return {...pose, alpha: alpha * wing.alpha * clamp01((elapsedMs - igniteMs) / 120) * (1 - afterImpact(elapsedMs, 180))};
        },
      }));
    });
    elements.push(wingElement({
      textureKey: WINGED_FLIGHT_TEXTURE_KEYS.WING, order: -5,
      pose: (local, elapsedMs) => {
        const {wing, ...pose} = resolveWingSpritePose(side, elapsedMs);
        return {...pose, alpha: wing.alpha};
      },
    }));
    elements.push(wingElement({
      textureKey: WINGED_FLIGHT_TEXTURE_KEYS.WING, blend: 'add', order: -4,
      pose: (local, elapsedMs) => {
        const {wing, ...pose} = resolveWingSpritePose(side, elapsedMs);
        // В полете перо вспыхивает на ударе вниз
        const strokeFlash = wing.heat === 0 && elapsedMs < impactMs
          ? WING.strokeFlashAlpha * clamp01((1 - resolveFlapWave(elapsedMs)) / 2)
          : 0;
        return {...pose, alpha: wing.alpha * Math.max(wing.heat, strokeFlash)};
      },
    }));
  });

  // ВЗЛЕТ: вспышка, лучи, волна и разлет искр
  {
    const {flashSizeFactor, flashMs, raysSizeFactor, raysMs, ringMs, ringToFactor, sparkCount, sparkReachFactor, sparkSizeFactor, sparkLifeMs} = WINGED_FLIGHT.launch;
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: pale, sizePx: size * flashSizeFactor,
      startMs: igniteMs - 70, durationMs: flashMs, blend: 'add', order: 7,
      pose: (local) => ({...start, scale: 0.5 + 0.7 * easeOutCubic(local), alpha: fadeInOut(local, 0.22, 0.7)}),
    }));
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: gold, sizePx: size * raysSizeFactor,
      startMs: igniteMs - 70, durationMs: raysMs, blend: 'add', order: 7,
      pose: (local) => ({...start, scale: 0.35 + 0.65 * easeOutCubic(local), rotation: 0.7 * local, alpha: 0.9 * fadeInOut(local, 0.12, 0.65)}),
    }));
    [CELL_OUTLINE_TEXTURE_KEYS.GLOW, HEX_WAVE_TEXTURE_KEYS.CORE].forEach((textureKey, index) => {
      elements.push(createElement({
        textureKey, tint: index ? pale : gold, sizePx: ringSizePx * hexScale(1),
        startMs: igniteMs, durationMs: ringMs, layer: 'ground', order: 5 + index,
        pose: (local) => ({
          ...start,
          scale: lerp(0.9, ringToFactor, easeOutCubic(local)),
          rotation: hexRotation,
          alpha: 1 - local * local,
        }),
      }));
    });
    for (let index = 0; index < sparkCount; index += 1) {
      const angle = rng() * TWO_PI;
      const reach = size * sparkReachFactor * (0.5 + 0.5 * rng());
      elements.push(createElement({
        textureKey: MELEE_TEXTURE_KEYS.BURST, tint: pickSparkTint(index), blend: 'add',
        sizePx: size * sparkSizeFactor, startMs: igniteMs, durationMs: sparkLifeMs * (0.7 + 0.3 * rng()), order: 8,
        pose: (local) => {
          const travelled = easeOutCubic(local);
          return {
            x: start.x + Math.cos(angle) * reach * travelled,
            y: start.y + Math.sin(angle) * reach * travelled * 0.75,
            scale: 1 - 0.65 * local,
            rotation: angle + local * 3,
            alpha: 1 - local * local,
          };
        },
      }));
    }
  }

  // СЛЕД: комета за отрядом и две тонкие нити от кончиков крыльев. Хвост — там, где
  // отряд был только что, поэтому на посадке он сам подтягивается к голове
  const pushTrail = ({tint, thicknessPx, lagMs, offset = {x: 0, y: 0}, alpha, order, blend = 'add'}) => {
    elements.push(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.STREAK, tint, sizePx: STREAK_BASE_PX, blend, layer: 'wings', order,
      startMs: igniteMs, durationMs: flightMs + WINGED_FLIGHT.trail.tailMs,
      pose: (local, elapsedMs) => {
        const head = resolveUnitPose(elapsedMs);
        const tail = resolveUnitPose(elapsedMs - lagMs);
        const length = Math.hypot(head.x - tail.x, head.y - tail.y);
        const liftY = (head.lift + tail.lift) / 2;
        return {
          x: (head.x + tail.x) / 2 + offset.x,
          y: (head.y + tail.y) / 2 - liftY + offset.y,
          rotation: flightRotation,
          scaleX: length / STREAK_BASE_PX,
          scaleY: thicknessPx / (STREAK_BASE_PX * STREAK_ASPECT),
          layer: resolveWingsLayer(elapsedMs),
          alpha: length > 0.5 ? alpha : 0,
        };
      },
    }));
  };
  {
    const {lagMs, coreThicknessFactor, wideThicknessFactor, underlayTint, underlayAlpha, tipLagMs, tipThicknessFactor, tipOffsetFactor, tipOffsetVerticalFactor, alpha} = WINGED_FLIGHT.trail;
    pushTrail({tint: underlayTint, thicknessPx: size * wideThicknessFactor, lagMs, alpha: underlayAlpha, order: -9.6, blend: undefined});
    pushTrail({tint: gold, thicknessPx: size * wideThicknessFactor, lagMs, alpha: alpha * 0.55, order: -9.5});
    pushTrail({tint: pale, thicknessPx: size * coreThicknessFactor, lagMs, alpha, order: -9.4});
    const tipOffset = size * (tipOffsetFactor + tipOffsetVerticalFactor * Math.abs(directionY));
    [1, -1].forEach((side) => {
      pushTrail({
        tint: light,
        thicknessPx: size * tipThicknessFactor,
        lagMs: tipLagMs,
        offset: {x: -directionY * side * tipOffset, y: directionX * side * tipOffset},
        alpha,
        order: -9.3,
      });
    });
  }

  // ИСКРЫ И ПЕРЬЯ сыплются там, где отряд пролетел
  {
    const {perHexSize, minCount, maxCount, spreadFactor, sizeFactor, lifeMs, fallFactor} = WINGED_FLIGHT.sparks;
    const count = Math.round(Math.min(maxCount, Math.max(minCount, perHexSize * distanceInHexSizes)));
    for (let index = 0; index < count; index += 1) {
      const emitMs = igniteMs + flightMs * ((index + rng()) / count);
      const origin = resolveUnitPose(emitMs);
      const offsetX = (rng() - 0.5) * 2 * size * spreadFactor;
      const offsetY = (rng() - 0.5) * size * spreadFactor;
      const fall = size * fallFactor * (0.6 + 0.4 * rng());
      const twinkle = rng() * TWO_PI;
      // Часть искр густого золота без сложения: на светлой земле сложение их съедает
      const isSolid = index % 3 === 0;
      elements.push(createElement({
        textureKey: MELEE_TEXTURE_KEYS.BURST, tint: isSolid ? gold : pickSparkTint(index), blend: isSolid ? undefined : 'add',
        sizePx: size * sizeFactor * (0.6 + 0.6 * rng()), startMs: emitMs, durationMs: lifeMs * (0.65 + 0.35 * rng()), order: 4,
        pose: (local) => ({
          x: origin.x + offsetX,
          y: origin.y - origin.lift + offsetY + fall * local * local,
          scale: 1 - 0.7 * local,
          rotation: twinkle + local * 2.5,
          alpha: (1 - local) * (0.65 + 0.35 * Math.sin(twinkle + local * 18)),
        }),
      }));
    }
  }
  {
    const {perHexSize, minCount, maxCount, sizeFactor, lifeMs, spreadFactor, fallFactor, swayFactor, alpha} = WINGED_FLIGHT.feathers;
    const count = Math.round(Math.min(maxCount, Math.max(minCount, perHexSize * distanceInHexSizes)));
    for (let index = 0; index < count; index += 1) {
      const side = index % 2 ? 1 : -1;
      const emitMs = igniteMs + flightMs * ((index + 0.5) / count);
      const origin = resolveUnitPose(emitMs);
      const offsetX = side * size * spreadFactor * (0.55 + 0.45 * rng());
      const offsetY = (rng() - 0.6) * size * 0.4;
      const swayPhase = rng() * TWO_PI;
      const startRotation = side * (0.6 + rng() * 0.8);
      elements.push(createElement({
        textureKey: WINGED_FLIGHT_TEXTURE_KEYS.FEATHER, tint: index % 3 === 2 ? light : gold,
        sizePx: size * sizeFactor * (0.8 + 0.4 * rng()), startMs: emitMs, durationMs: lifeMs * (0.8 + 0.2 * rng()), order: 3,
        pose: (local) => {
          const sway = Math.sin(swayPhase + local * 5);
          return {
            x: origin.x + offsetX + size * swayFactor * sway,
            y: origin.y - origin.lift + offsetY + size * fallFactor * local,
            scale: 1 - 0.25 * local,
            rotation: startRotation + 0.7 * sway,
            alpha: alpha * clamp01(local * 8) * (1 - clamp01((local - 0.55) / 0.45)),
          };
        },
      }));
    }
  }

  // ПОСАДКА: вспышка, лучи, волны по ячейке и фонтан искр
  {
    const {flashSizeFactor, flashMs, raysSizeFactor, raysMs, auraSizeFactor, auraAlpha, ringDelaysMs, ringMs, ringToFactor, sparkCount, sparkReachFactor, sparkSizeFactor, sparkLifeMs} = WINGED_FLIGHT.land;
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: gold, sizePx: size * auraSizeFactor,
      startMs: impactMs - 120, durationMs: landMs + 120, layer: 'ground', blend: 'add',
      pose: (local) => ({...to.ground, scale: 0.6 + 0.4 * easeOutQuad(local), alpha: auraAlpha * fadeInOut(local, 0.2, 0.7)}),
    }));
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: pale, sizePx: size * flashSizeFactor,
      startMs: impactMs - 40, durationMs: flashMs, blend: 'add', order: 7,
      pose: (local) => ({...end, scale: 0.5 + 0.7 * easeOutCubic(local), alpha: fadeInOut(local, 0.2, 0.7)}),
    }));
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: gold, sizePx: size * raysSizeFactor,
      startMs: impactMs - 40, durationMs: raysMs, blend: 'add', order: 7,
      pose: (local) => ({...end, scale: 0.35 + 0.65 * easeOutCubic(local), rotation: -0.6 * local, alpha: 0.95 * fadeInOut(local, 0.1, 0.6)}),
    }));
    ringDelaysMs.forEach((delayMs, ringIndex) => {
      [CELL_OUTLINE_TEXTURE_KEYS.GLOW, HEX_WAVE_TEXTURE_KEYS.CORE].forEach((textureKey, index) => {
        elements.push(createElement({
          textureKey, tint: index ? pale : gold, sizePx: ringSizePx * hexScale(1),
          startMs: impactMs + delayMs, durationMs: ringMs, layer: 'ground', order: 5 + index + ringIndex,
          pose: (local) => ({
            ...end,
            scale: lerp(0.85, ringToFactor, easeOutCubic(local)),
            rotation: hexRotation,
            alpha: 1 - local * local,
          }),
        }));
      });
    });
    for (let index = 0; index < sparkCount; index += 1) {
      // Фонтан: искры расходятся вверх и в стороны, затем оседают
      const angle = -Math.PI / 2 + (rng() - 0.5) * Math.PI * 1.5;
      const reach = size * sparkReachFactor * (0.45 + 0.55 * rng());
      const fall = size * 0.5;
      elements.push(createElement({
        textureKey: MELEE_TEXTURE_KEYS.BURST, tint: pickSparkTint(index), blend: 'add',
        sizePx: size * sparkSizeFactor * (0.6 + 0.6 * rng()), startMs: impactMs, durationMs: sparkLifeMs * (0.7 + 0.3 * rng()), order: 8,
        pose: (local) => {
          const travelled = easeOutCubic(local);
          return {
            x: end.x + Math.cos(angle) * reach * travelled,
            y: end.y + Math.sin(angle) * reach * travelled + fall * local * local,
            scale: 1 - 0.65 * local,
            rotation: angle + local * 3,
            alpha: 1 - local * local,
          };
        },
      }));
    }
  }

  const totalMs = Math.max(impactMs + landMs, ...elements.map((element) => element.endMs));
  return {totalMs, impactMs, elements, outlines: [], resolveUnitPose};
};
